import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/auth/useAccessToken";
import { encodeMailLinkState } from "./mailLink";

/**
 * Connect's view of the user's Mail calendar, straight from the Mail BFF (the
 * access token carries every resource audience, and the Mail BFF allows any
 * https origin). Read-only here except for calendar settings — working hours
 * and time zone are one per user, shared by both calendars.
 */

const mailBase = import.meta.env.VITE_MAIL_BFF_BASE_URL ?? "http://localhost:5102";
export const mailWebUrl = import.meta.env.VITE_MAIL_WEB_BASE_URL ?? "http://localhost:5180";

export interface MailCalendarEvent {
    id: string;
    subject: string;
    startUtc: string;
    endUtc: string;
    isAllDay: boolean;
    location?: string | null;
    description?: string | null;
    /** manual | invite | email */
    source: string;
    /** meeting | flight | train | … */
    kind?: string | null;
    /** confirmed | tentative | cancelled */
    status: string;
    sourceMessageId?: string | null;
    organizer?: string | null;
    /** needs-action | accepted | tentative | declined */
    response?: string | null;
    /** Set when this is the emailed copy of a Connect meeting — shown once, as the meeting. */
    connectMeetingId?: string | null;
    /** One occurrence of a repeating event (the list is expanded server-side). */
    isRecurring?: boolean | null;
}

export interface MailCalendarSettings {
    eventsFromEmail: boolean;
    /** ISO weekdays, 1 = Monday. */
    workDays: number[];
    workStart: string;
    workEnd: string;
    timeZone: string;
}

async function call<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(new URL(path, mailBase).toString(), { ...init, headers, credentials: "omit" });
    if (!res.ok) {
        let message = `Mail BFF ${path} → ${res.status}`;
        try {
            const problem = (await res.json()) as { title?: string };
            if (problem.title) message = problem.title;
        } catch { /* not JSON */ }
        throw new Error(message);
    }
    return (await res.json()) as T;
}

export const mailCalendarApi = {
    /** Events in [from, to), repeating ones expanded into occurrences (window ≤ 100 days). */
    listEvents: (from: Date, to: Date, token: string | undefined) =>
        call<MailCalendarEvent[]>(
            `/api/calendar/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&expand=true`,
            token),
    getSettings: (token: string | undefined) => call<MailCalendarSettings>("/api/calendar/settings", token),
    putSettings: (patch: Partial<MailCalendarSettings>, token: string | undefined) =>
        call<MailCalendarSettings>("/api/calendar/settings", token, { method: "PUT", body: JSON.stringify(patch) })
};

export function useMailCalendarSettings() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["mail-calendar-settings", token],
        queryFn: () => mailCalendarApi.getSettings(token),
        enabled: Boolean(token),
        staleTime: 5 * 60_000,
        retry: 1
    });
}

export function usePutMailCalendarSettings() {
    const token = useAccessToken();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: Partial<MailCalendarSettings>) => mailCalendarApi.putSettings(patch, token),
        onSuccess: (data) => qc.setQueryData(["mail-calendar-settings", token], data)
    });
}

/** Deep link to the email an event was found in (Mail's `folder:uid` id). */
export function mailMessageUrl(sourceMessageId: string): string {
    const i = sourceMessageId.lastIndexOf(":");
    const folder = i > 0 ? sourceMessageId.slice(0, i) : "inbox";
    // One opaque token instead of readable ?folder=&m= (webmail decodes it; see mailLink.ts).
    return `${mailWebUrl}/mail?s=${encodeMailLinkState({ folder, m: sourceMessageId })}`;
}
